import { AuthLayout } from '@/bundle/Auth/ui/AuthLayout/AuthLayout';
import { AuthStep } from '@/bundle/Auth/ui/AuthStep/AuthStep';
import { useTranslation } from 'react-i18next';
import { Typography } from '@/components/Typography/Typography';
import { COLORS } from '@/styles/colors';
import { useFormik } from 'formik';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import {
  LOGIN_ERROR_NOTIFICATION_OFFSET,
  LOGIN_VALIDATION_SCHEMA,
} from '@/bundle/Auth/LoginFlow/LoginPage/const/const';
import { Box } from '@/components/Box/Box';
import { Button } from '@/components/Button/Button';
import { useLocation, useSearchParams } from 'react-router';
import { Notification } from '@/components/Notification/Notification';
import { GoogleReCaptcha, GoogleReCaptchaType } from '@/components/GoogleReCaptcha/GoogleReCaptcha';
import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { login } from './api';
import { useAuthNavigateToStep } from '@/bundle/Auth/hooks/useAuthNavigateToStep';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { LoginType } from '@/api/v1/users/public/loginApi';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { NavigationLink } from '@/components/NavigationLink/NavigationLink';
import { getSignUpPath } from '../../AdminOnboardingFlow/SignUpPage/path';

const LOGIN_EMAIL_QUERY_PARAM = 'email';

export const LoginPage = () => {
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const navigateToStep = useAuthNavigateToStep();
  const recaptchaRef = useRef<GoogleReCaptchaType>(null);
  const { t } = useTranslation(['login', 'form']);

  const userEmail = searchParams.get(LOGIN_EMAIL_QUERY_PARAM);

  const [captchaLoading, setCaptchaLoading] = useState(false);

  useEffect(() => {
    // Clear expired history state on page reload
    if (state?.sessionExpired) {
      window.history.replaceState(null, '');
    }

    // Clear account blocked state on page reload
    if (state?.accountBlocked) {
      window.history.replaceState(null, '');
    }

    // Clear recipient account exists state on page reload
    if (state?.recipientAccountsExists) {
      window.history.replaceState(null, '');
    }
  }, [state?.sessionExpired, state?.accountBlocked, state?.recipientAccountsExists]);

  const { mutate, data, isPending } = useMutation({
    mutationKey: ['login'],
    mutationFn: (payload: LoginType) => {
      return login(payload);
    },
    onSuccess(response, variables) {
      if (response?.error) {
        recaptchaRef.current?.resetCaptcha();

        return;
      }

      amplitudeService.logEvent(AMPLITUDE_EVENTS.LogInEmailPwdCheckSuccess);

      return navigateToStep(response?.body, { state: { email: variables?.email } });
    },
    retry: false,
  });

  const formik = useFormik({
    initialValues: { email: userEmail || '' },
    validationSchema: LOGIN_VALIDATION_SCHEMA,
    onSubmit: (values) => {
      setCaptchaLoading(true);

      recaptchaRef.current
        ?.executeCaptcha()
        .then((token) => {
          setCaptchaLoading(false);

          const payload = { email: values.email, recaptcha_response: token };

          mutate(payload);
        })
        .catch((error) => {
          console.error('google captcha failed with', error?.message);

          setCaptchaLoading(false);
        });
    },
  });

  const error = data?.error;
  const formError = getResponseError(error) || getResponseError(error, 'recaptcha_response');

  return (
    <AuthLayout>
      <AuthStep>
        <Typography variant='headline4' fontWeight='semibold' color={COLORS.grey[900]} marginBottom='24px'>
          {t('login:loginHeader')}
        </Typography>

        {state?.sessionExpired && !error && (
          <Notification variant='error' {...LOGIN_ERROR_NOTIFICATION_OFFSET}>
            {t('login:sessionExpiredNotification')}
          </Notification>
        )}

        {state?.accountBlocked && !error && (
          <Notification variant='error' {...LOGIN_ERROR_NOTIFICATION_OFFSET}>
            {t('login:forbiddenAccessNotification')}
          </Notification>
        )}

        {state?.recipientAccountsExists && !error && (
          <Notification variant='info' {...LOGIN_ERROR_NOTIFICATION_OFFSET}>
            Looks like you already have an account with us! Please log in to continue.
          </Notification>
        )}

        {formError && (
          <Notification variant='error' {...LOGIN_ERROR_NOTIFICATION_OFFSET}>
            {formError}
          </Notification>
        )}
        <FormikForm value={formik}>
          <FormikInput
            label={t('form:label.email')}
            placeholder={t('form:placeholder.email')}
            name='email'
            apiError={error}
          />
          <GoogleReCaptcha ref={recaptchaRef} />
          <Box marginTop='12px'>
            <Button type='submit' isLoading={isPending || captchaLoading}>
              {t('login:continueButtonLabel')}
            </Button>
          </Box>
          <Box marginTop='20px'>
            <Typography variant='bodySmall'>
              New to WireVault? <NavigationLink to={getSignUpPath()}>Create your WireVault account</NavigationLink>{' '}
            </Typography>
          </Box>
        </FormikForm>
      </AuthStep>
    </AuthLayout>
  );
};
